// src/components/Products.js
// require("dotenv").config();
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import "./Products.css"; // Style your products here
import { useAuth } from "../AuthContext";
// import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter } from "@fortawesome/free-solid-svg-icons";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { faShoppingCart } from "@fortawesome/free-solid-svg-icons";
import ProductModal from "./ProductModal";
import OrderModal from "./OrderModal";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { faHeart as solidHeart } from "@fortawesome/free-solid-svg-icons";
import { faHeart as regularHeart } from "@fortawesome/free-regular-svg-icons";

const confirmNavigation = (message, callback) => {
  const confirmLeave = window.confirm(message);
  callback(confirmLeave);
};

const Products = () => {
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1); // Assuming your API uses page numbers
  const [loading, setLoading] = useState(false);
  const loader = useRef(null);
  const [selectedCompany, setSelectedCompany] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [shouldLoadMore, setShouldLoadMore] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [quantities, setQuantities] = useState({});
  const [order, setOrder] = useState([]);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const navigate = useNavigate(); // Hook for navigation
  const { isLoggedIn, mobile, userType } = useAuth();
  const [isOrderFetched, setIsOrderFetched] = useState(false);
  const [discontinuedIds, setDiscontinuedIds] = useState([]);
  const [discontinuedProductsMap, setDiscontinuedProductsMap] = useState({});
  const [selectedSegment, setSelectedSegment] = useState("All");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showFavorites, setShowFavorites] = useState(false);
  const [favoriteProductIds, setFavoriteProductIds] = useState(new Set());
  const [showFavoritesMessage, setShowFavoritesMessage] = useState(false);
  const [caseSizes, setCaseSizes] = useState({});
  const [productPrices, setProductPrices] = useState({});

  // Reference for the search input
  const searchInputRef = useRef(null);

  // Handle scrolling to blur the input
  const handleTouchMove = useCallback(() => {
    if (searchInputRef.current) {
      searchInputRef.current.blur();
    }
  }, []);

  useEffect(() => {
    if (userType === "employee") {
      axios
        .get(`${process.env.REACT_APP_API_URL}/api/product-prices`)
        .then((response) => {
          const pricesMap = {};
          response.data.forEach((item) => {
            const [companyCode, productId] = item.product_id.split("-");
            if (!pricesMap[companyCode]) {
              pricesMap[companyCode] = {};
            }
            pricesMap[companyCode][productId] = item.price;
          });
          setProductPrices(pricesMap);
        })
        .catch((error) => {
          console.error("Error fetching product prices:", error);
        });
    }
  }, [userType]);

  useEffect(() => {
    // Add touchmove event listener to blur the input on scroll
    window.addEventListener("touchmove", handleTouchMove);

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener("touchmove", handleTouchMove);
    };
  }, [handleTouchMove]);

  useEffect(() => {
    if (showFavorites && favoriteProductIds.size == 0) {
      setShowFavoritesMessage(true);
    } else {
      setShowFavoritesMessage(false);
    }
  }, [showFavorites, favoriteProductIds]);

  const fetchFavorites = () => {
    if (!isLoggedIn || !mobile) {
      return;
    }

    axios
      .get(`${process.env.REACT_APP_API_URL}/api/favorites/${mobile}`)
      .then((response) => {
        const favoritesSet = new Set(
          response.data.map((item) => `${item.product_id}-${item.company}`)
        );
        setFavoriteProductIds(favoritesSet);
      })
      .catch((error) => {
        console.error("Error fetching favorite products:", error);
      });
  };

  useEffect(() => {
    fetchFavorites();
  }, [isLoggedIn, mobile]); // Refetch when user logs in or changes

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const fetchCaseSizes = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/case_sizes`
      );
      const sizes = response.data.reduce(
        (acc, item) => ({
          ...acc,
          [item.Product_id + "-" + item.Company]: item.Case_Size,
        }),
        {}
      );
      setCaseSizes(sizes);
    } catch (error) {
      console.error("Error fetching case sizes:", error);
    }
  };

  useEffect(() => {
    fetchCaseSizes();
  }, []);

  const handleDownload = () => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/products/download`, {
        params: {
          company: selectedCompany,
          segment: selectedSegment,
          searchTerm: searchTerm,
        },
      })
      .then((response) => {
        const { data: products } = response;
        const pdf = new jsPDF();

        // Define the columns and their titles
        const columns = [
          { header: "S.No", dataKey: "sno" },
          { header: "Brand Name", dataKey: "brand_name" },
          { header: "Composition", dataKey: "composition" },
          { header: "Packing", dataKey: "packing" },
          { header: "MRP", dataKey: "mrp" },
          { header: "Company", dataKey: "company" },
          { header: "Category", dataKey: "category" },
        ];

        // Map products data to match columns
        const rows = products.map((product, index) => ({
          sno: index + 1,
          brand_name: product.brand_name,
          composition: product.compostion,
          packing: product.packing,
          mrp: product.mrp + "/-",
          company: product.company,
          category: product.category,
        }));

        pdf.autoTable(columns, rows, {
          styles: { font: "helvetica", fontSize: 10, cellPadding: 2 },
          headStyles: { fillColor: [22, 160, 133] }, // Teal color
          bodyStyles: {},
          alternateRowStyles: {
            fillColor: [240, 240, 240], // Light gray for better contrast and readability
          },
          columnStyles: {
            sno: { halign: "center" },
            composition: { cellWidth: 60, fontSize: 9 },
            category: { fontSize: 7 },
            mrp: { halign: "left", cellWidth: 20 },
          },
          margin: { top: 10 },
          theme: "grid",
        });

        pdf.save(
          "products-" +
            selectedCompany.replaceAll(" ", "-") +
            "-" +
            selectedSegment.replaceAll(" ", "-") +
            (searchTerm ? "-" + searchTerm.replaceAll(" ", "-") : "") +
            ".pdf"
        );
      })
      .catch((error) => {
        console.error("Error downloading products:", error);
      });
  };

  const handleDownloadWithPrice = () => {
    axios
      .get(
        `${process.env.REACT_APP_API_URL}/api/products/download-with-price`,
        {
          params: {
            company: selectedCompany,
            segment: selectedSegment,
            searchTerm: searchTerm,
          },
        }
      )
      .then((response) => {
        const { data: products } = response;
        const pdf = new jsPDF();

        // Define the columns and their titles
        const columns = [
          { header: "S.No", dataKey: "sno" },
          { header: "Brand Name", dataKey: "brand_name" },
          { header: "Composition", dataKey: "composition" },
          { header: "Packing", dataKey: "packing" },
          { header: "MRP", dataKey: "mrp" },
          { header: "Price", dataKey: "price" },
          { header: "Company", dataKey: "company" },
          { header: "Category", dataKey: "category" },
        ];

        // Map products data to match columns
        const rows = products.map((product, index) => ({
          sno: index + 1,
          brand_name: product.brand_name,
          composition: product.compostion,
          packing: product.packing,
          mrp: product.mrp + "/-",
          price: product.price + "/-",
          company: product.company,
          category: product.category,
        }));

        pdf.autoTable(columns, rows, {
          styles: { font: "helvetica", fontSize: 10, cellPadding: 2 },
          headStyles: { fillColor: [22, 160, 133] }, // Teal color
          bodyStyles: {},
          alternateRowStyles: {
            fillColor: [240, 240, 240], // Light gray for better contrast and readability
          },
          columnStyles: {
            sno: { halign: "center", cellWidth: 10 },
            brand_name: { cellWidth: 30 },
            composition: { cellWidth: 40, fontSize: 7 },
            category: { fontSize: 7, cellWidth: 20 },
            packing: { cellWidth: 20 },
            mrp: { halign: "left", cellWidth: 20 },
            price: { halign: "left", cellWidth: 15 },
            company: { cellWidth: 20 },
          },
          margin: { top: 10 },
          theme: "grid",
        });

        pdf.save(
          "products-with-price-" +
            selectedCompany.replaceAll(" ", "-") +
            "-" +
            selectedSegment.replaceAll(" ", "-") +
            (searchTerm ? "-" + searchTerm.replaceAll(" ", "-") : "") +
            ".pdf"
        );
      })
      .catch((error) => {
        console.error("Error downloading products with price:", error);
      });
  };

  const toggleFavorite = (product) => {
    if (!isLoggedIn) {
      navigate("/login");
      return;
    }

    const method = product.isFavorite ? "delete" : "post";
    const productKey = `${product.id}-${product.company}`;

    let finalData;

    if (method == "delete") {
      finalData = {
        data: {
          mobile: mobile,
          product_id: product.id,
          company: product.company,
        },
      };
    } else {
      finalData = {
        mobile: mobile,
        product_id: product.id,
        company: product.company,
      };
    }

    axios[method](`${process.env.REACT_APP_API_URL}/api/favorites`, finalData)
      .then(() => {
        const newFavorites = new Set(favoriteProductIds);
        if (product.isFavorite) {
          newFavorites.delete(productKey);
        } else {
          newFavorites.add(productKey);
        }
        setFavoriteProductIds(newFavorites);
        showPopupMessage(
          `Favorite ${product.isFavorite ? "removed" : "added"}`
        );
        // Restore the scroll position after the state update
        product.isFavorite = !product.isFavorite;
      })
      .catch((error) => {
        console.error("Error toggling favorite:", error);
        showPopupMessage("Error updating favorite status");
      });
  };

  useEffect(() => {
    // Load discontinued product IDs
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/discontinued-products`)
      .then((response) => {
        setDiscontinuedIds(response.data);
      })
      .catch((error) => {
        console.error("Error fetching discontinued products:", error);
      });
  }, []);

  // Load products from the API
  useEffect(() => {
    loadProducts();
  }, [discontinuedIds]); // Reload products when discontinuedIds changes

  useEffect(() => {
    if (discontinuedIds.length > 0) {
      const map = {};
      discontinuedIds.forEach((id) => {
        const [companyCode, productId] = id.split("-");
        if (!map[companyCode]) {
          map[companyCode] = new Set();
        }
        map[companyCode].add(productId);
      });
      setDiscontinuedProductsMap(map);
    }
  }, [discontinuedIds]);

  useEffect(() => {
    if (isLoggedIn && mobile) {
      axios
        .get(`${process.env.REACT_APP_API_URL}/api/order/incomplete/${mobile}`)
        .then((response) => {
          const fetchedOrder = response.data.order;
          const fetchedItems = response.data.items;

          setOrder(
            fetchedItems.map((item) => ({
              productId: item.product_id,
              quantity: item.quantity,
              company: item.company,
              brandName: item.brandName,
              packing: item.packing,
              // Add additional fields as needed, possibly fetching from 'products' state by 'productId'
            }))
          );

          // Update 'quantities' state
          const newQuantities = {};
          fetchedItems.forEach((item) => {
            newQuantities[`${item.product_id}-${item.company}`] = item.quantity;
          });
          setQuantities(newQuantities);
          setIsOrderFetched(true);
        })
        .catch((error) => {
          console.error("Error fetching incomplete order:", error);
        });
    }
    // Add 'products' to the dependency array to ensure this runs when products are updated
    // Be cautious as this might introduce additional re-renders or dependency cycles depending on your data fetching strategy
  }, [isLoggedIn, mobile]);

  // Add this useEffect hook
  useEffect(() => {
    // Check if there are changes to save (i.e., order is not empty)
    if (isLoggedIn && mobile && isOrderFetched) {
      saveIncompleteOrder();
    }
  }, [order, isOrderFetched]); // Add dependencies to the useEffect hook

  const saveIncompleteOrder = () => {
    if (isLoggedIn && mobile) {
      axios
        .post(
          `${process.env.REACT_APP_API_URL}/api/order/incomplete/${mobile}`,
          { orderItems: order }
        )
        .then((response) => {
          console.log("Incomplete order saved/updated successfully");
        })
        .catch((error) => {
          console.error("Error saving/updating incomplete order:", error);
        });
    }
  };

  //   useEffect(() => {
  //     const handleBeforeUnload = (event) => {
  //       if (order.length > 0) {
  //         // Cancel the event as stated by the standard.
  //         event.preventDefault();
  //         // Chrome requires returnValue to be set.
  //         event.returnValue =
  //           "Are you sure you want to leave? All the items in the order will be deleted.";
  //       }
  //     };

  //     if (order.length > 0) {
  //       window.addEventListener("beforeunload", handleBeforeUnload);
  //     }

  //     return () => {
  //       window.removeEventListener("beforeunload", handleBeforeUnload);
  //     };
  //   }, [order]); // Depend on the 'order' state

  const showPopupMessage = (message) => {
    setPopupMessage(message);
    setIsPopupVisible(true);
    setTimeout(() => {
      setIsPopupVisible(false);
    }, 4000); // Hide the popup after 3 seconds
  };

  const handleOrderSuccess = () => {
    setIsOrderModalOpen(false); // Close the modal
    setOrder([]); // Clear the order state
    setQuantities({}); // Optionally, clear the quantities if needed
  };

  useEffect(() => {
    if (isOrderModalOpen) {
      // Save the current overflow value to restore it later
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = "hidden";
      return () => (document.body.style.overflow = originalStyle); // Cleanup function to reset the style
    }
  }, [isOrderModalOpen]); // This effect depends on isOrderModalOpen

  const handleInputClick = (e) => {
    e.stopPropagation(); // This prevents the click event from bubbling up to the parent div
  };

  // Function to open modal with product details
  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  // Function to add an item to the order
  const addToOrder = (product) => {
    if (!isLoggedIn || userType === "employee") {
      navigate("/login"); // Redirect user to login page
      return; // Prevent further execution
    }
    const { id, company, quantity } = product;
    // console.log(product);

    setOrder((currentOrder) => {
      const orderIndex = currentOrder.findIndex(
        (item) => item.productId === id && item.company === company
      );

      if (orderIndex >= 0) {
        // Product exists in the order, update its quantity
        const updatedOrder = [...currentOrder];
        updatedOrder[orderIndex] = { ...updatedOrder[orderIndex], quantity };
        return updatedOrder;
      } else {
        // Product doesn't exist, add it to the order
        return [
          ...currentOrder,
          {
            productId: id,
            company,
            quantity,
            brandName: product.brand_name,
            packing: product.packing,
          },
        ];
      }
    });

    showPopupMessage(
      `${product.brand_name} (${product.quantity} ${product.unit}) added to order`
    );
  };

  // Function to close the modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  useEffect(() => {
    // Handles click outside the sidebar
    const handleClickOutside = (event) => {
      if (
        isSidebarVisible &&
        loader.current &&
        !loader.current.contains(event.target)
      ) {
        setIsSidebarVisible(false);
      }
    };

    // Attach the event listener
    document.addEventListener("mousedown", handleClickOutside);

    // Cleanup the event listener on component unmount
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isSidebarVisible, loader]);

  const adjustQuantity = (product, delta, e) => {
    e.stopPropagation(); // Prevent opening the modal or any other undesired behavior
    const productKey = `${product.id}-${product.company}`;
    const currentQuantity = quantities[productKey] || 0;
    const newQuantity = Math.max(0, currentQuantity + delta);

    updateQuantity(product, newQuantity);
  };

  const getImagePath = (company, imageName) => {
    // Base URL for images in production - adjust the domain as necessary
    let basePath = "";

    // console.log(company);

    // Determine the base path based on the company
    switch (company) {
      case "Trumac":
        basePath = "/app_admin/assets/images/products";
        break;
      case "Camrut":
        basePath = "/camrut_app_admin/assets/images/products";
        break;
      case "Medisync":
        basePath = "/medisync_app_admin/assets/images/products";
        break;
      case "Cialto Drugs":
        basePath = "/cialto_app_admin/assets/images/products";
        break;
      case "Pythus Pharma":
        basePath = "/pythus_app_admin/assets/images/products";
        break;
      default:
        basePath = "/images"; // Default path for development or fallback
    }

    // console.log(basePath);

    // Check if the environment is development or production
    // This is a basic check, you might need to adjust it based on your setup
    const isDevelopment =
      !process.env.NODE_ENV || process.env.NODE_ENV === "development";

    // console.log("mode : ", process.env.NODE_ENV);

    // Return the correct path
    return isDevelopment
      ? `https://trumachealthcare.net${basePath}/${imageName}`
      : `${basePath}/${imageName}`;
  };

  useEffect(() => {
    if (products.length > 0 && !initialLoadComplete) {
      setInitialLoadComplete(true);
    }
  }, [products]);

  const handleObserver = (entities) => {
    const target = entities[0];
    if (target.isIntersecting && !loading && initialLoadComplete && hasMore) {
      console.log("Changing page");
      setPage((prevPage) => prevPage + 1);
      setShouldLoadMore(true); // Indicate that it's appropriate to load more products
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "20px",
      threshold: 1.0,
    });
    if (loader.current) {
      observer.observe(loader.current);
    }

    // Cleanup observer on component unmount
    return () => {
      if (loader.current) {
        observer.unobserve(loader.current);
      }
    };
  }, [handleObserver]);

  useEffect(() => {
    // Reset products and load from the first page whenever filters change
    // setPage(1);
    // setProducts([]);

    console.log("Load prods initiated 1", selectedCompany, searchTerm);
    loadProducts(1, selectedCompany, selectedSegment, searchTerm);
  }, [selectedCompany, searchTerm, selectedSegment, showFavorites]);

  // useEffect for pagination
  useEffect(() => {
    if (page > 1 && shouldLoadMore) {
      loadProducts(page, selectedCompany, selectedSegment, searchTerm);
      setShouldLoadMore(false); // Reset the flag after loading
    }
  }, [
    page,
    shouldLoadMore,
    selectedCompany,
    searchTerm,
    selectedSegment,
    showFavorites,
  ]);

  const handleFilterChange = (company) => {
    setSelectedCompany(company);
    // setSearchTerm(""); // Clear search term when changing company
  };

  const handleSegmentChange = (segment) => {
    setSelectedSegment(segment);
  };

  const handleSearchChange = (e) => {
    const newSearchTerm = e.target.value;
    setPage(1);
    // Use the debounced function to update the search term
    // debouncedSearchTerm(newSearchTerm);
    setSearchTerm(newSearchTerm);
  };

  const loadProducts = (
    pageParam = page,
    company = selectedCompany,
    segment = "",
    searchTerm = ""
  ) => {
    if (pageParam === 1) {
      // Clear products for the first page load or when company/searchTerm changes
      setProducts([]);
    }

    setLoading(true);
    const params = new URLSearchParams({
      page: pageParam,
      company,
      segment,
      searchTerm,
      favoritesOnly: showFavorites,
    }).toString();
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/products?${params}`)
      .then((response) => {
        let newProducts = response.data.map((product) => {
          // Check if the product is discontinued
          const companyCode = getCompanyCode(product.company);
          const productKey = `${product.id}-${product.company}`;
          const isDiscontinued =
            discontinuedProductsMap[companyCode] &&
            discontinuedProductsMap[companyCode].has(product.id.toString());

          return {
            ...product,
            isDiscontinued,
            isFavorite: favoriteProductIds.has(productKey),
          };
        });

        if (showFavorites) {
          newProducts = newProducts.filter((product) => product.isFavorite);
        }

        // If fewer products are returned than your fetch limit, it's the end
        if (newProducts.length < 5) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }

        // Sort by brand_name in ascending order
        const sortedNewProducts = newProducts.sort((a, b) =>
          a.brand_name.localeCompare(b.brand_name)
        );

        // Deduplicate only if adding to an existing list (i.e., pagination)
        const updatedProducts =
          pageParam > 1
            ? deduplicateProducts([...products, ...sortedNewProducts])
            : sortedNewProducts;

        setProducts(updatedProducts);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching products:", error);
        setLoading(false);
      });
  };

  const getCompanyCode = (companyName) => {
    switch (companyName) {
      case "Trumac":
        return "TMHAPP";
      case "Camrut":
        return "CAMAPP";
      case "Medisync":
        return "MEDAPP";
      case "Cialto Drugs":
        return "CIAAPP";
      case "Pythus Pharma":
        return "PYAPP";
      default:
        return "";
    }
  };

  // Utility function to deduplicate products based on product_image
  const deduplicateProducts = (products) => {
    return Array.from(
      new Map(
        products.map((product) => [product.product_image, product])
      ).values()
    );
  };

  const removeFromOrder = (productId, company) => {
    setQuantities((prevQuantities) => {
      const newQuantities = { ...prevQuantities };
      delete newQuantities[`${productId}-${company}`];
      return newQuantities;
    });
    setOrder((currentOrder) =>
      currentOrder.filter(
        (item) => !(item.productId === productId && item.company === company)
      )
    );
  };

  const handleShowFavoritesChange = (e) => {
    const shouldShowFavorites = e.target.checked;

    if (!isLoggedIn && shouldShowFavorites) {
      // User is not logged in and trying to show favorites
      navigate("/login"); // Redirect to the login page
    } else {
      setShowFavorites(shouldShowFavorites);
      setPage(1);
      setSelectedCompany("All");
      setSelectedSegment("All");
    }
  };

  const updateQuantity = (product, newQuantity) => {
    const productKey = `${product.id}-${product.company}`;

    // Update the quantity in the state
    setQuantities((prevQuantities) => ({
      ...prevQuantities,
      [productKey]: newQuantity,
    }));

    // If the new quantity is more than 0, update/add the product in the order
    if (newQuantity > 0) {
      addToOrder({ ...product, quantity: newQuantity });
    } else {
      // If the new quantity is 0, remove the product from the order
      removeFromOrder(product.id, product.company);
    }
  };

  const handleQuantityChange = (product, e) => {
    const newQuantity = parseInt(e.target.value, 10) || 0;
    updateQuantity(product, newQuantity);
  };

  return (
    <div className="products-layout">
      {/* Toggle Button */}
      {order.length > 0 &&
        window.innerWidth <= 768 &&
        userType !== "employee" && (
          <button
            className="cart-icon"
            onClick={() => setIsOrderModalOpen(true)}
            aria-label="View Order"
          >
            <FontAwesomeIcon icon={faShoppingCart} />
          </button>
        )}
      <button
        className="toggle-sidebar-btn"
        onClick={() => setIsSidebarVisible(!isSidebarVisible)}
        aria-label="Toggle Filters"
      >
        <FontAwesomeIcon icon={faFilter} />
      </button>
      <div className={`filters-sidebar ${isSidebarVisible ? "visible" : ""}`}>
        {/* Close Button */}
        <button
          className="close-sidebar-btn"
          onClick={() => setIsSidebarVisible(false)}
          aria-label="Close Filters"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
        {userType !== "employee" && (
          <label className="show-favorites">
            <input
              type="checkbox"
              checked={showFavorites}
              onChange={handleShowFavoritesChange}
            />
            My Favorite Products
          </label>
        )}
        <h4>Company Name</h4>
        <label>
          <input
            type="radio"
            name="company"
            checked={selectedCompany === "Trumac Healthcare"}
            onChange={() => handleFilterChange("Trumac Healthcare")}
          />
          Trumac Healthcare
        </label>
        <label>
          <input
            type="radio"
            name="company"
            checked={selectedCompany === "Camrut Pharma"}
            onChange={() => handleFilterChange("Camrut Pharma")}
          />
          Camrut Pharma
        </label>
        <label>
          <input
            type="radio"
            name="company"
            checked={selectedCompany === "Medisync Pharma"}
            onChange={() => handleFilterChange("Medisync Pharma")}
          />
          Medisync Pharma
        </label>
        <label>
          <input
            type="radio"
            name="company"
            checked={selectedCompany === "Cialto Drugs"}
            onChange={() => handleFilterChange("Cialto Drugs")}
          />
          Cialto Drugs
        </label>
        <label>
          <input
            type="radio"
            name="company"
            checked={selectedCompany === "Pythus Pharma"}
            onChange={() => handleFilterChange("Pythus Pharma")}
          />
          Pythus Pharma
        </label>
        <label>
          <input
            type="radio"
            name="company"
            checked={selectedCompany === "All"}
            onChange={() => handleFilterChange("All")}
          />
          All
        </label>
        {order.length > 0 && userType !== "employee" && (
          <button
            className="view-order-btn"
            onClick={() => setIsOrderModalOpen(true)}
          >
            View Order
          </button>
        )}
        <h4>Segment</h4>
        {[
          "All",
          "Cardiac Diabetic",
          "Derma",
          "Paediatric",
          "Gynae",
          "Ortho",
          "Ophthalmic",
          "Injectable",
          "ENT",
          "Herbal",
          "Dental",
          "Urology",
          "Nephro",
          "Neuro",
        ].map((segment) => (
          <label key={segment}>
            <input
              type="radio"
              name="segment"
              checked={selectedSegment === segment}
              onChange={() => handleSegmentChange(segment)}
            />
            {segment}
          </label>
        ))}
      </div>
      {userType !== "employee" && (
        <OrderModal
          isOpen={isOrderModalOpen}
          onClose={() => setIsOrderModalOpen(false)}
          order={order}
          setOrder={setOrder} // Pass the setOrder function down
          quantities={quantities}
          setQuantities={setQuantities} // Pass the setQuantities function down
          removeItemFromOrder={removeFromOrder}
          onOrderSuccess={handleOrderSuccess}
        />
      )}
      <div className="products-page">
        <div className={`search-container ${isModalOpen ? "hidden" : ""}`}>
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search products by brand name, composition, category anything (Just start typing what you want)........"
            value={searchTerm}
            onChange={handleSearchChange}
            className="search-input"
          />
          <button onClick={handleDownload} className="download-btn">
            {isMobile ? <FontAwesomeIcon icon={faDownload} /> : "Download PDF"}
          </button>
          {userType == "employee" && (
            <button onClick={handleDownloadWithPrice} className="download-btn">
              {isMobile ? (
                <FontAwesomeIcon icon={faDownload} />
              ) : (
                "Download PDF With Price"
              )}
            </button>
          )}
        </div>
        {showFavoritesMessage && (
          <div className="favorites-message">
            You don't have any Favorite Product Marked yet, kindly click on the
            heart shaped icon besides the Brand Name to mark product as your
            favorite.
          </div>
        )}
        <div className="products-container">
          {products.map((product) => {
            const productKey = `${product.id}-${product.company}`;
            const caseSize = caseSizes[productKey] || "";
            const packingDescription = product.packing
              .replace(/Case Size.*$/i, "")
              .trim();
            const companyCode = getCompanyCode(product.company);
            let price =
              productPrices[companyCode] &&
              productPrices[companyCode][product.id];

            // if (price == undefined && product.company == "Camrut") {
            //   const trumacProduct = products.find(
            //     (p) =>
            //       p.companyName === "Camrut" &&
            //       p.product_image === product.product_image
            //   );

            //   if (trumacProduct) {
            //     price =
            //       productPrices["TMHAPP"] &&
            //       productPrices["TMHAPP"][trumacProduct.id];
            //   }
            // }

            return (
              <div
                key={`${product.id}-${product.company}`}
                className={`product-card ${
                  product.isDiscontinued ? "discontinued" : ""
                }`}
                onClick={() => handleProductClick(product)} // Use the handleProductClick function to set the selected product and open the modal
              >
                <img
                  src={getImagePath(product.company, product.product_image)}
                  alt={product.brand_name}
                  className="product-image"
                />
                <div className="product-info">
                  <h3 className="product-name">
                    {product.brand_name}
                    {userType !== "employee" && (
                      <FontAwesomeIcon
                        icon={product.isFavorite ? solidHeart : regularHeart}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevents the product modal from opening
                          toggleFavorite(product); // Toggles favorite status
                        }}
                        className={`favorite-icon ${
                          product.isFavorite ? "favorite" : ""
                        }`}
                      />
                    )}
                  </h3>
                  {product.isDiscontinued && (
                    <span className="discontinued-notice">Discontinued</span>
                  )}
                  <p className="product-composition">{product.composition}</p>
                  <p className="product-packing">
                    <strong>Packing:</strong> {packingDescription}
                  </p>
                  {caseSize != "" && (
                    <p className="product-case-size">
                      <strong>Case Size:</strong> {caseSize} {product.unit}
                    </p>
                  )}
                  <p className="product-mrp">
                    <strong>MRP:</strong> ₹{product.mrp}/- per {product.unit}
                  </p>
                  {userType === "employee" && price !== undefined && (
                    <p className="product-price">
                      <strong>Price:</strong> ₹{price}/- per {product.unit}
                    </p>
                  )}
                  <p className="product-company">
                    <strong>Company:</strong> {product.company}
                  </p>
                  <p className="product-segment">
                    <strong>Category:</strong> {product.category}
                  </p>
                </div>
                {userType !== "employee" && !product.isDiscontinued && (
                  <div className="quantity-controls">
                    <button onClick={(e) => adjustQuantity(product, -1, e)}>
                      -
                    </button>
                    <input
                      type="text" // Change to type number to allow direct input
                      value={
                        quantities[`${product.id}-${product.company}`] || "0"
                      }
                      onChange={(e) => handleQuantityChange(product, e)} // New handler for direct input
                      onClick={handleInputClick} // Add this line
                    />
                    <button onClick={(e) => adjustQuantity(product, 1, e)}>
                      +
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          {isPopupVisible && (
            <div className="popup-message">{popupMessage}</div>
          )}
          {loading && <div>Loading...</div>}
          <div ref={loader} />
        </div>
      </div>
      <ProductModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        product={selectedProduct}
      />
    </div>
  );
};

export default Products;
